import { useMemo, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  alsoApplyTargets,
  draftRoundTypes,
  formatDraftWeekLine,
  handOffAxis,
  PRESET_FIELDS,
  PRESET_IDS,
  presetOfRule,
  presetValuesFor,
  previewRule,
  programRoundLabel,
  rulesForRows,
  slotRefKey,
  sheetScaleOf,
  startPresetFor,
  type Draft,
  type DraftLineOptions,
  type EffortScale,
  type MaxForProposal,
  type PresetBase,
  type PresetField,
  type PresetId,
  type PresetValues,
  type RowTarget,
  type ProgressionRule,
  type SlotRef,
} from "@heraion/shared";

import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { PageSheet } from "../primitives/PageSheet";
import type { UiTheme } from "../theme";
import { RuleAlsoApply } from "./RuleAlsoApply";
import { RuleField } from "./RuleFields";
import { ruleAt, ruleOptions } from "./ruleCache";
import { RuleWarnings } from "./RuleWarnings";
import type { RuleWords } from "./ruleWords";
import type { V3Styles } from "./v3Styles";

// The «Rule» sheet (#336 slice 5, brief §5.4b): the presets, the fields
// of the one picked (each with its step), the rule in words, the preview
// round by round with «changes» on the rounds that change, then the summary
// and «Apply». The preview is the draft Apply writes (previewRule), so the two
// cannot disagree. For several exercises it lists them instead of rounds. A
// rule no preset writes, or a row by hand, opens with none picked and «Apply» off.
// Mounted with a key per target list: it opens on the first one's rule.
// «Also apply to:» (#351) adds rows to the targets: the sheet's parameters are
// shared, each row's start (load, effort) stays its own (rulesForRows).

export function RuleSheet({
  theme,
  styles,
  draft,
  refs,
  lineOptions,
  scale,
  words,
  nameOf,
  dayOf,
  lockedThrough,
  maxOf,
  onApply,
  onClose,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  refs: readonly SlotRef[];
  lineOptions: DraftLineOptions;
  /** The athlete's scale: the effort axis of a row that has none. */
  scale: EffortScale;
  words: RuleWords;
  /** A row's exercise name. */
  nameOf: (ref: SlotRef) => string;
  /** A day's label. */
  dayOf: (day: number) => string;
  /** Rounds 0..lockedThrough were executed: the rule leaves them (§5.2.4). */
  lockedThrough: number;
  /** A row's MAX: a load preset on a row with no load starts from its
   *  reference load. */
  maxOf: (ref: SlotRef) => MaxForProposal | undefined;
  /** The rule of each row, in `rows` order (the targets, then what was ticked). */
  onApply: (rules: readonly ProgressionRule[], rows: readonly SlotRef[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const local = useMemo(() => makeStyles(theme), [theme]);
  const first = refs[0]!;
  const unit = lineOptions.cellUnit;
  const opts = ruleOptions(unit);
  const slot0 = draft.sessions[first.day]?.slots[first.slot];
  const rec0 = ruleAt(draft, first.day, first.slot, opts.loadQuantum);
  // The preset on the rule's own axis, else the row's default: never one
  // that would rewrite the row on another axis unasked.
  const startId = startPresetFor(slot0, rec0);
  // The effort axis the sheet reads and writes: the row's own (an RPE row
  // stays RPE for a RIR athlete), the athlete's only for a row with none.
  const axisScale = sheetScaleOf(slot0, rec0, scale);
  // null: a rule no preset writes, the sheet opens with none picked.
  const [id, setId] = useState<PresetId | null>(startId);
  const max = maxOf(first);
  const presetRef = { cellUnit: unit, types: draftRoundTypes(draft), ...(max ? { max } : {}) };
  const [opened] = useState<PresetValues>(() =>
    slot0 && startId ? presetValuesFor(startId, slot0, rec0, axisScale, presetRef) : {},
  );
  const [values, setValues] = useState<PresetValues>(opened);
  // The values the anchors are compared with: a row keeps its own start unless
  // the athlete moved it here.
  const [baseline, setBaseline] = useState<PresetValues>(opened);
  const [extra, setExtra] = useState<SlotRef[]>([]);
  // Only the rows that exist: refs and rules stay paired by index.
  const allRefs = useMemo(
    () => [...refs, ...extra].filter((r) => draft.sessions[r.day]?.slots[r.slot] !== undefined),
    [refs, extra, draft],
  );
  // On the recognized rule's own preset that rule is the base (presetRuleOn).
  const [base] = useState<PresetBase | undefined>(() =>
    startId && rec0.kind === "rule" && presetOfRule(rec0.name, rec0.rule) === startId
      ? { rule: rec0.rule, values: opened }
      : undefined,
  );
  const pick = (next: PresetId) => {
    setId(next);
    if (!slot0) return;
    const picked = presetValuesFor(next, slot0, rec0, axisScale, presetRef);
    setValues(picked);
    setBaseline(picked);
  };
  const rules = useMemo(() => {
    if (!id) return null;
    const rows = allRefs.map(
      (r): RowTarget => ({ slot: draft.sessions[r.day]!.slots[r.slot]!, rec: ruleAt(draft, r.day, r.slot, opts.loadQuantum), max: maxOf(r) }),
    );
    const ref = { cellUnit: unit, types: draftRoundTypes(draft) };
    return rulesForRows(id, values, baseline, rows, axisScale, ref, id === startId ? base : undefined);
  }, [id, values, baseline, allRefs, draft, opts, maxOf, unit, axisScale, startId, base]);
  const rule = rules?.[0] ?? null;
  const preview = useMemo(
    () => (rules ? previewRule(draft, allRefs, rules, { ...opts, lockedThrough }) : null),
    [draft, allRefs, rules, lockedThrough, opts],
  );
  const alsoTargets = useMemo(() => alsoApplyTargets(draft, refs), [draft, refs]);
  const ticked = useMemo(() => new Set(extra.map(slotRefKey)), [extra]);
  const toggle = (r: SlotRef) =>
    setExtra((old) => (old.some((x) => slotRefKey(x) === slotRefKey(r)) ? old.filter((x) => slotRefKey(x) !== slotRefKey(r)) : [...old, r]));
  // The cells by hand in the rounds the rule writes: when the rule changes
  // axis an intensity on another axis goes back to it, the rest stays.
  let hand = 0;
  let offAxis = 0;
  for (const [i, r] of allRefs.entries()) {
    const rec = ruleAt(draft, r.day, r.slot, opts.loadQuantum);
    const slot = draft.sessions[r.day]?.slots[r.slot];
    const mine = rules?.[i];
    if (rec.kind === "manual" || !slot || !mine) continue;
    const open = rec.exceptions.filter((e) => e.week > lockedThrough);
    hand += open.length;
    if (rec.rule.axis !== mine.axis) offAxis += handOffAxis(slot, open, mine.axis).length;
  }
  const changed = new Set((preview?.diffs ?? []).flatMap((d) => d.rounds.flatMap((c, w) => (c ? [w] : [])))).size;
  const set = (key: string, v: number | number[]) => setValues((old) => ({ ...old, [key]: v }));
  const field = (f: PresetField) => (
    <RuleField
      key={f.key}
      theme={theme}
      styles={styles}
      field={f}
      presetId={id}
      values={values}
      axisScale={axisScale}
      lineOptions={lineOptions}
      words={words}
      onChange={set}
    />
  );

  // The ladder shows the one exercise; the list names every target once there are several.
  const listed = allRefs.length === 1 ? [] : allRefs;
  // No preset picked: the rows as they are, nothing changes.
  const nextSlot = preview ? preview.draft.sessions[first.day]?.slots[first.slot] : slot0;
  const changesWord = t("editorV3.rule.changes", { defaultValue: "changes" });
  const byHand = rec0.kind === "manual" && startId === null;
  const pickWord = byHand
    ? t("editorV3.rule.pickPresetManual", { defaultValue: "No rule recognized. Pick a preset to replace the values by hand." })
    : t("editorV3.rule.pickPreset", { defaultValue: "No preset writes this rule. Pick one to replace it." });
  const notes = rule
    ? [
        hand - offAxis > 0
          ? t("editorV3.rule.handStays", { count: hand - offAxis, defaultValue: "The {{count}} cells by hand stay." })
          : null,
        offAxis > 0
          ? t("editorV3.rule.handAxisResets", {
              count: offAxis,
              defaultValue: "{{count}} values typed by hand are replaced: the new rule uses another unit.",
            })
          : null,
        lockedThrough >= 0
          ? t("editorV3.rule.lockedStay", {
              round: programRoundLabel(lockedThrough + 1),
              defaultValue: "Done up to {{round}}: those rounds stay as they are.",
            })
          : null,
        extra.length > 0
          ? t("editorV3.rule.alsoEach", { defaultValue: "Each exercise keeps its own starting numbers." })
          : null,
        preview && preview.members.length > 0
          ? t("editorV3.rule.supersetFollows", { defaultValue: "The superset takes the same set count." })
          : null,
        t("editorV3.rule.undoable", { defaultValue: "Undo brings it back." }),
      ].filter(Boolean)
    : [];
  return (
    <PageSheet
      theme={theme}
      visible
      onRequestClose={onClose}
      title={
        allRefs.length === 1
          ? t("editorV3.rule.sheetTitle", { defaultValue: "Rule" })
          : t("editorV3.rule.sheetTitleMany", { count: allRefs.length, defaultValue: "Rule for {{count}} exercises" })
      }
      cancelLabel={t("program.cancel", { defaultValue: "Cancel" })}
      action={{
        label: t("editorV3.rule.apply", { defaultValue: "Apply" }),
        // Off until a preset is picked: a rule no preset writes stays as it is.
        disabled: !rule,
        onPress: () => {
          if (!rules) return;
          onApply(rules, allRefs);
          onClose();
        },
      }}
    >
      <ScrollView contentContainerStyle={local.body} keyboardShouldPersistTaps="handled">
        <ChipChoiceRow
          theme={theme}
          choices={PRESET_IDS}
          selected={id}
          onSelect={pick}
          label={(p) => words.presets[p]}
          chipA11yLabel={(p) => words.presets[p]}
          groupA11yLabel={t("editorV3.rule.presets", { defaultValue: "Presets" })}
          title={t("editorV3.rule.presets", { defaultValue: "Presets" })}
        />
        {id ? <View style={local.fields}>{PRESET_FIELDS[id].map(field)}</View> : null}
        <Text style={local.sentence}>
          {rule ? words.sentence(rule) : rec0.kind === "rule" ? words.sentence(rec0.rule) : byHand ? t("editorV3.rule.manual", { defaultValue: "Values by hand" }) : ""}
        </Text>
        {rules ? (
          <RuleAlsoApply
            theme={theme}
            sameExercise={alsoTargets.sameExercise}
            others={alsoTargets.others}
            ticked={ticked}
            onToggle={toggle}
            nameOf={nameOf}
            dayOf={dayOf}
          />
        ) : null}
        {refs.length === 1 && nextSlot ? (
          <View style={styles.ladder}>
            {Array.from({ length: draft.cycleWeeks }, (_, w) => {
              const line = formatDraftWeekLine(nextSlot, w, lineOptions);
              const ch = preview?.diffs[0]?.rounds[w] === true;
              const round = programRoundLabel(w + 1);
              const text = line ?? t("editorV3.notInRound", { defaultValue: "Not in this round" });
              return (
                <View
                  key={w}
                  style={styles.ladderRow}
                  accessible
                  accessibilityLabel={ch ? `${round}: ${text}, ${changesWord}` : `${round}: ${text}`}
                >
                  <Text style={styles.round}>{round}</Text>
                  <Text style={line === null ? styles.roundOff : styles.roundLine}>
                    {line === null ? text : keepSets(line)}
                  </Text>
                  {ch ? <Text style={local.changes}>{changesWord}</Text> : null}
                </View>
              );
            })}
          </View>
        ) : null}
        {listed.length > 0 ? (
          <View style={styles.ladder}>
            {listed.map((r, i) => {
              const n = preview?.diffs[i]?.rounds.filter(Boolean).length ?? 0;
              return (
                <View key={slotRefKey(r)} style={styles.ladderRow} accessible>
                  <Text style={styles.roundLine}>{nameOf(r)}</Text>
                  {preview ? (
                    <Text style={n > 0 ? local.changes : styles.muted}>
                      {n > 0
                        ? t("editorV3.rule.roundsChange", { count: n, defaultValue: "{{count}} rounds change." })
                        : t("editorV3.rule.noRoundChanges", { defaultValue: "No round changes." })}
                    </Text>
                  ) : null}
                </View>
              );
            })}
          </View>
        ) : null}
        {/* Only the count is live: the notes under it would be read again at every tap. */}
        <Text style={styles.muted} accessibilityLiveRegion="polite" aria-live="polite">
          {!rule
            ? pickWord
            : changed > 0
              ? t("editorV3.rule.roundsChange", { count: changed, defaultValue: "{{count}} rounds change." })
              : t("editorV3.rule.noRoundChanges", { defaultValue: "No round changes." })}
        </Text>
        {notes.length > 0 ? <Text style={styles.muted}>{notes.join(" ")}</Text> : null}
        {rules ? (
          <RuleWarnings
            styles={styles}
            draft={draft}
            refs={allRefs}
            names={allRefs.map(nameOf)}
            rules={rules}
            loadQuantum={opts.loadQuantum}
          />
        ) : null}
      </ScrollView>
    </PageSheet>
  );
}

/** A preview line that wraps between sets, never inside one («5 @ RPE 8»). */
const keepSets = (line: string): string =>
  line
    .split(" · ")
    .map((set) => set.replace(/ /g, "\u00A0"))
    .join(" · ");

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    body: { padding: s.s5, gap: s.s4 },
    fields: { gap: s.s2 },
    changes: { color: p.ink, fontSize: ty.sizes.sm, fontWeight: "700" },
    // Sans, as on the rule card: a sentence, not a prescription line.
    sentence: { color: p.ink2, fontSize: ty.sizes.sm },
  });
}
